import * as React from "react";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./card";
import { CheckCircle } from "lucide-react";

interface Feature {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description: React.ReactNode;
  items?: React.ReactNode[];
}

interface FeatureGridProps extends React.HTMLAttributes<HTMLDivElement> {
  features: Feature[];
  columns?: 2 | 3 | 4;
}

const FeatureGrid = React.forwardRef<HTMLDivElement, FeatureGridProps>(
  ({ className, features, columns = 3, ...props }, ref) => {
    const gridClasses = {
      2: "md:grid-cols-2",
      3: "md:grid-cols-2 lg:grid-cols-3", 
      4: "md:grid-cols-2 lg:grid-cols-4"
    };

    return (
      <div
        ref={ref}
        className={cn(
          "grid gap-8",
          gridClasses[columns],
          className
        )}
        {...props}
      >
        {features.map((feature, index) => (
          <Card 
            key={index}
            className="group h-full p-6 transition-all duration-300 hover:-translate-y-1 bg-card border border-border hover:border-primary hover:shadow-green"
          >
            <CardHeader className="p-0 pb-4">
              {feature.icon && (
                <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mb-3 group-hover:bg-primary/30 transition-colors border border-border group-hover:border-primary">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
              )}
              <CardTitle className="text-lg font-bold text-foreground mb-2">
                {feature.title}
              </CardTitle>
              <CardDescription className="text-muted-foreground text-sm">
                {feature.description}
              </CardDescription>
            </CardHeader>
            {feature.items && (
              <CardContent className="p-0">
                <ul className="space-y-1">
                  {feature.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="text-sm text-muted-foreground flex items-center">
                      <CheckCircle className="w-3 h-3 text-primary mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    );
  }
);
FeatureGrid.displayName = "FeatureGrid";

export { FeatureGrid, type Feature };